import Timer from '../functions/timers/timeout';
import Deferred from '../functions/promise';
import debug from 'debug';

const log = debug('ws');

export default {
  name: 'WebsocketsMixin',
  data() {
    return {
      wsSocket: null,
      wsSocketStatus: null,
      wsSocketReconnectData: null,
      wsMid: 0,
      wsWaiting: [],
      wsServer: process.env.VUE_APP_WS_URL,
      wsConnected: new Deferred(),
      wsLoggedIn: new Deferred(),
      wsToken: false,
      wsClosed: false,
      wsHeartbeatTimeout: 1000 * 60 * 1,
      wsSleepTimeout: 1000 * 60 * 2,
    };
  },
  async mounted() {
    this.safeConnectToWebsocketServer();
  },
  computed: {
    wsIsConnected() {
      return !this.wsClosed && !this.wsSocketReconnectData;
    },
  },
  watch: {
    wsToken() {
      this.safeConnectToWebsocketServer();
    },
    wsServer() {
      this.safeConnectToWebsocketServer();
    },
  },
  methods: {
    safeConnectToWebsocketServer() {
      clearTimeout(this._wsSafeConnecting);
      this._wsSafeConnecting = setTimeout(this.connectToWebsocketServer.bind(this), 500);
    },
    connectToWebsocketServer() {
      this.wsSocketStatus = this.wsSocket === null ? 'connecting' : 'reconnecting';

      if (this.wsSocketReconnectData) {
        clearInterval(this.wsSocketReconnectData.intervalID);
      }

      const wsServer = this.wsServer
        .replace('http://', 'ws://')
        .replace('https://', 'wss://');
      log('connecting to chat server', wsServer, this.wsToken);

      this.wsClosed = false;

      this.wsSocket = new WebSocket(wsServer);
      this.wsSocket.onopen = async () => {
        clearTimeout(this._wsSafeConnecting);
        this.wsSocketStatus = 'connected';
        this.wsSocketReconnectData = null;
        this.wsConnected.resolve(true);
        if (this.wsToken) {
          log('Connection established, logging in');
          const response = await this.sendOnWebsocket({
            type: 'login',
            authentication: `Token ${this.wsToken}`,
          });
          log('logged in');
          this.$emit('ws:login');
          this.wsLoggedIn.resolve(response);
        } else {
          log('no login');
          this.wsLoggedIn.resolve(false);
        }
      };
      this.wsSocket.onmessage = async (event) => {
        this.resetHeartbeat();
        const data = JSON.parse(event.data);
        log('>>', data.type);

        // respond to waiting events
        if (data.mid) {
          const responder = this.wsWaiting.find(t => t.mid === data.mid);
          if (responder) {
            responder.handler(data);
          } else {
            log(`unknown response`, data.mid, data);
          }
        }

        // send to components listening to $root
        this.$emit(`ws:${data.type}`, data);
        this.$emit(`ws:_message_`, data);
      };
      this.wsSocket.onclose = (event) => {
        this.wsConnected.resolve(false);

        if (event.wasClean) {
          this.wsSocketStatus = 'closed_cleanly';
          log(`Connection closed cleanly, code=${event.code} reason=${event.reason}`);
        } else {
          this.wsSocketStatus = 'closed';
          log('Connection died');
        }

        // retry
        if (!this.wsClosed) {
          this.wsConnected.reset();
          this.wsLoggedIn.reset();
          if (this.wsSocketReconnectData) {
            let countdown = 0;
            const attempts = Math.min(this.wsSocketReconnectData.attempt, 30);
            countdown = this.getWebsocketReconnectCountdown(attempts);

            this.wsSocketReconnectData.attempt += 1;
            this.wsSocketReconnectData.countdown = countdown;
          } else {
            this.wsSocketReconnectData = {
              attempt: 1,
              countdown: this.getWebsocketReconnectCountdown(1),
              intervalID: null,
            };
          }

          this.wsSocketReconnectData.intervalID = setInterval(this.wsSocketReconnectCountdown.bind(this), 100);
          log('retry connection in', this.wsSocketReconnectData.countdown);
        } else {
          log('leaving connection closed');
        }
      };
      this.wsSocket.onerror = (error) => {
        log(`[error] ${error.message}`);
      };

      return this.wsLoggedIn.promise;
    },

    async sendOnWebsocket(data, timeout = 10000) {
      this.resetHeartbeat();

      // login is required to finish connection
      if (data.type === 'login') {
        await this.wsConnected.promise;
      } else {
        await this.wsLoggedIn.promise;
      }

      this.wsMid += 1;

      // eslint-disable-next-line no-param-reassign
      data.mid = this.wsMid;

      log('<<', data.type);
      this.wsSocket.send(JSON.stringify(data));

      return Promise.race([
        new Promise(r => setTimeout(() => r({ ok: false, error: 'Application Timeout' }), timeout)),
        new Promise(r => this.wsWaiting.push({ mid: data.mid, handler: r })),
      ]);
    },

    async resetHeartbeat() {
      if (await Timer.begin('ws:heartbeat', this.wsHeartbeatTimeout)) {
        this.sendOnWebsocket({ type: '♥' });
      }
    },

    wsSocketReconnectCountdown() {
      // log('countdown', this.wsSocketReconnectData.countdown);
      if (this.wsSocketReconnectData && this.wsSocketReconnectData.countdown > 0) {
        this.wsSocketReconnectData.countdown -= 0.1;

        if (this.wsSocketReconnectData.countdown < 0) {
          clearInterval(this.wsSocketReconnectData.intervalID);
          this.connectToWebsocketServer();
        }
      }
    },

    getWebsocketReconnectCountdown(attempts = 1) {
      // returns value in seconds
      return 0.5 * (attempts + Math.random() * attempts);
    },
  },
};
